// AdminLinkTracking — personal share-link tracking viewer.
// Data lives in the shared tracking store; access is granted to this
// TailorWiz admin session by the report endpoint (owner allowlist).
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Link2, Loader2, RefreshCw, ArrowLeft, MousePointerClick } from "lucide-react";

const REPORT_URL = "https://zjkzrobirkkrlmrfwshw.supabase.co/functions/v1/link-report";
const REPORT_ANON = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inpqa3pyb2JpcmtrcmxtcmZ3c2h3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzg4OTU5MDksImV4cCI6MjA5NDQ3MTkwOX0.Jy9Ks96_ikcc_pvauKg5C8WP532sI1QE4YVysybzHh0";

type Summary = { code: string; sites: string[]; clicks: number; landings: number; first: string; last: string };
type TrackEvent = { site: string; path: string; event: string; created_at: string };

const fmt = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export default function AdminLinkTracking() {
  const [summary, setSummary] = useState<Summary[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [events, setEvents] = useState<TrackEvent[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load(code?: string) {
    setLoading(true);
    setError(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      if (!token) throw new Error("Please sign in again.");
      const res = await fetch(REPORT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: REPORT_ANON, Authorization: `Bearer ${token}` },
        body: JSON.stringify(code ? { code } : {}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error ?? "Failed to load");
      if (code) setEvents((data?.events ?? []) as TrackEvent[]);
      else setSummary((data?.summary ?? []) as Summary[]);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);
  useEffect(() => { if (selected) { setEvents(null); void load(selected); } }, [selected]);

  return (
    <div className="p-6">
      <div className="bg-white rounded-xl border border-slate-200 p-5 max-w-5xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {selected ? (
              <button onClick={() => setSelected(null)} className="flex items-center gap-1.5 text-[13px] font-bold text-[#0B1C3F] hover:text-[#B8923F]">
                <ArrowLeft className="w-4 h-4" /> All links
              </button>
            ) : (
              <h3 className="flex items-center gap-2 text-[16px] font-extrabold text-slate-900">
                <Link2 className="w-4 h-4 text-[#B8923F]" /> Link Tracking
              </h3>
            )}
            {selected && <span className="font-extrabold text-[15px] text-slate-900">· {selected}</span>}
          </div>
          <button
            onClick={() => void (selected ? load(selected) : load())}
            className="flex items-center gap-1.5 text-[12px] font-bold text-slate-500 hover:text-[#0B1C3F] border border-slate-200 rounded-lg px-3 py-1.5"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        <p className="text-[12.5px] text-slate-500 mb-4">
          Share links like <code className="bg-slate-100 px-1.5 py-0.5 rounded">tailorwiz.com/?r=john-smith</code> or{" "}
          <code className="bg-slate-100 px-1.5 py-0.5 rounded">jobintel360.com/?r=john-smith</code>. Every click and page
          they visit shows here. Watch the full session in Microsoft Clarity → Recordings → filter tag <b>ref</b>.
        </p>

        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-[13px] text-red-700 mb-3">{error}</div>}
        {loading && !summary && !events && (
          <div className="flex items-center gap-2 text-slate-500 text-[14px] py-10 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {!selected && summary && (
          summary.length === 0 ? (
            <p className="text-[13px] text-slate-400 py-8 text-center">No link clicks yet — share your first ?r= link!</p>
          ) : (
            <table className="w-full text-left text-[13px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                  <th className="py-2 pr-4">Link code</th>
                  <th className="py-2 pr-4">Sites</th>
                  <th className="py-2 pr-4 text-center">Link clicks</th>
                  <th className="py-2 pr-4 text-center">Pages viewed</th>
                  <th className="py-2 pr-4">First seen</th>
                  <th className="py-2">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((s) => (
                  <tr key={s.code} onClick={() => setSelected(s.code)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                    <td className="py-2.5 pr-4 font-extrabold text-[#0B1C3F]">{s.code}</td>
                    <td className="py-2.5 pr-4 text-slate-600">{s.sites.join(", ")}</td>
                    <td className="py-2.5 pr-4 text-center font-bold text-[#B8923F]">{s.landings}</td>
                    <td className="py-2.5 pr-4 text-center text-slate-700">{s.clicks}</td>
                    <td className="py-2.5 pr-4 text-slate-500">{fmt(s.first)}</td>
                    <td className="py-2.5 text-slate-500">{fmt(s.last)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {selected && events && (
          events.length === 0 ? (
            <p className="text-[13px] text-slate-400 py-8 text-center">No events for this code.</p>
          ) : (
            <div className="space-y-1.5">
              {events.map((e, i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2">
                  <MousePointerClick className={`w-4 h-4 shrink-0 ${e.event === "landing" ? "text-[#B8923F]" : "text-slate-400"}`} />
                  <span className={`text-[11px] font-extrabold uppercase tracking-wider w-16 shrink-0 ${e.event === "landing" ? "text-[#B8923F]" : "text-slate-400"}`}>
                    {e.event === "landing" ? "Click" : "Page"}
                  </span>
                  <span className="text-[12px] font-bold text-slate-500 w-16 shrink-0">{e.site}</span>
                  <span className="text-[13px] font-semibold text-slate-900 flex-1 truncate">{e.path}</span>
                  <span className="text-[12px] text-slate-500 shrink-0">{fmt(e.created_at)}</span>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
